import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Eye, 
  Edit, 
  Trash2,
  FileText,
  Calendar,
  User,
  AlertCircle,
  Settings
} from 'lucide-react';
import apiService from '../../services/api';
import { showSuccess, showError, showDeleteConfirm } from '../../utils/toast';
import { formatPrice } from '../../utils/currency';
import { useAuth } from '../Auth/AuthContext';
import { hasPermission } from '../../utils/permissions';
import NewApplicationModal from './NewApplicationModal';
import EditApplicationModal from './EditApplicationModal';
import ViewApplicationModal from './ViewApplicationModal';

const Applications = () => {
  const applicationStatuses = [
    { value: 'draft', label: 'Draft' },
    { value: 'awaiting_payment', label: 'Awaiting Payment' },
    { value: 'partially_paid', label: 'Partially Paid' },
    { value: 'under_review', label: 'Under Review' },
    { value: 'action_required', label: 'Action Required' },
    { value: 'fully_paid', label: 'Fully Paid' },
    { value: 'submitted_to_authority', label: 'Submitted to Authority' },
    { value: 'appointment_scheduled', label: 'Appointment Scheduled' },
    { value: 'processing', label: 'Processing' },
    { value: 'admission_offered', label: 'Admission Offered' },
    { value: 'booking_confirmed', label: 'Booking Confirmed' },
    { value: 'withdrawn_cancelled', label: 'Withdrawn / Cancelled' },
    { value: 'successful', label: 'Successful' },
    { value: 'unsuccessful', label: 'Unsuccessful' },
    { value: 'ready_for_collection', label: 'Ready for Collection' },
    { value: 'delivered', label: 'Delivered' },
  ];

  const { user } = useAuth();
  const [showNewApplicationModal, setShowNewApplicationModal] = useState(false);
  const [showEditApplicationModal, setShowEditApplicationModal] = useState(false);
  const [showViewApplicationModal, setShowViewApplicationModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [applications, setApplications] = useState([]);
  const [showOnlyMyApplications, setShowOnlyMyApplications] = useState(false);
  const [currentStaffId, setCurrentStaffId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusCounts, setStatusCounts] = useState({ total: 0 });

  // Load current staff ID for logged-in staff user
  useEffect(() => {
    const loadCurrentStaff = async () => {
      try {
        if (!user || user.role !== 'staff' || !user.email) return;
        const params = {
          page: 1,
          limit: 5,
          search: user.email,
          status: 'all'
        };
        const response = await apiService.getStaff(params);
        const staffList = response.staff || [];
        const matched = staffList.find(
          (m) => m.email && m.email.toLowerCase() === user.email.toLowerCase()
        );
        if (matched) {
          setCurrentStaffId(matched.id);
        }
      } catch (err) {
        console.error('Error loading current staff info:', err);
      }
    };

    loadCurrentStaff();
  }, [user]);

  // Load applications on mount and when filters change
  useEffect(() => {
    loadApplications();
  }, [statusFilter, priorityFilter, searchTerm]);

  const loadApplications = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Build params object - only include defined values
      const params = {
        page: 1,
        limit: 100
      };
      
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      
      // Only send status/priority if they're not 'all'
      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      if (priorityFilter && priorityFilter !== 'all') {
        params.priority = priorityFilter;
      }
      
      const response = await apiService.getApplications(params);
      console.log('Applications API response:', response);
      
      // Transform API data to match frontend format
      const transformedApplications = (response.applications || []).map(app => {
        console.log('Transforming application:', app);

        // Determine source from application_source (DB) first, then fallbacks
        const source = (app.application_source || '').toLowerCase().replace(/\s/g, '_');
        let sourceType = 'Other';
        let sourceLabel = 'System';

        if (source === 'self-applied' || source === 'self_applied') {
          sourceType = 'Customer';
          sourceLabel = 'Self-applied';
        } else if (app.creator_role === 'agent' || source === 'agent' || app.agent_user_id) {
          sourceType = 'Agent';
          sourceLabel = app.creator_name || app.agent_name || 'Agent';
        } else if (app.creator_role === 'staff' || app.staff_id) {
          sourceType = 'Staff';
          sourceLabel = app.creator_name || app.staff_name || (app.staff_id ? `Staff #${app.staff_id}` : 'Staff');
        } else if (app.creator_role === 'admin' || source === 'staff') {
          sourceType = 'Admin';
          sourceLabel = app.creator_name || 'Admin / Back Office';
        } else {
          sourceType = 'Admin';
          sourceLabel = 'Admin / Back Office';
        }

        return {
          id: app.id,
          customerName: `${app.first_name || ''} ${app.last_name || ''}`.trim(),
          customerEmail: app.email || '',
          customerPhone: app.phone || '',
          service: app.service_name || '',
          status: app.status || 'draft',
          priority: app.priority || 'normal',
          createdAt: app.created_at ? new Date(app.created_at).toLocaleDateString() : '',
          updatedAt: app.updated_at ? new Date(app.updated_at).toLocaleDateString() : '',
          assignedTo: app.assigned_user_name || app.staff_name || '',
          notes: app.notes || '',
          documents: app.documents || [],
          estimated_completion_date: app.estimated_completion_date,
          actual_completion_date: app.actual_completion_date,
          sourceType,
          sourceLabel,
          // For API calls / additional info
          customer_id: app.customer_id,
          service_id: app.service_id,
          staff_id: app.staff_id,
          agent_user_id: app.agent_user_id,
          creator_name: app.creator_name,
          creator_role: app.creator_role
        };
      });
      
      console.log('Transformed applications:', transformedApplications);
      setApplications(transformedApplications);
      
      // Calculate status counts
      const counts = transformedApplications.reduce((acc, app) => {
        const key = app.status || 'draft';
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, { total: transformedApplications.length });
      setStatusCounts(counts);
    } catch (err) {
      console.error('Error loading applications:', err);
      setError(err.message || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const handleViewApplication = async (application) => {
    try {
      // Fetch full application details
      const response = await apiService.getApplication(application.id);
      const app = response.application;
      const selectedTierName = app.selected_tier_name || null;
      let selectedTierPrice = null;
      const tiers = app.service_pricing_tiers != null
        ? (Array.isArray(app.service_pricing_tiers) ? app.service_pricing_tiers : (typeof app.service_pricing_tiers === 'string' ? (() => { try { return JSON.parse(app.service_pricing_tiers); } catch { return []; } })() : []))
        : [];
      if (selectedTierName && tiers.length) {
        const tier = tiers.find(t => (t.name || '').trim() === (selectedTierName || '').trim());
        if (tier && (tier.price != null || tier.price === 0)) selectedTierPrice = tier.price;
      }
      const transformedApp = {
        id: app.id,
        customerName: `${app.first_name || ''} ${app.last_name || ''}`.trim(),
        customerEmail: app.email || '',
        customerPhone: app.phone || '',
        customerId: app.customer_id,
        serviceName: app.service_name || '',
        serviceId: app.service_id,
        status: app.status || 'draft',
        priority: app.priority || 'normal',
        createdAt: app.created_at ? new Date(app.created_at).toLocaleDateString() : '',
        updatedAt: app.updated_at ? new Date(app.updated_at).toLocaleDateString() : '',
        assignedTo: app.assigned_user_name || app.staff_name || '',
        staffId: app.staff_id,
        notes: app.notes || '',
        documents: app.documents || [],
        estimated_completion_date: app.estimated_completion_date,
        actual_completion_date: app.actual_completion_date,
        selectedTierName,
        selectedTierPrice: selectedTierPrice != null ? formatPrice(selectedTierPrice) : null
      };
      
      setSelectedApplication(transformedApp);
      setShowViewApplicationModal(true);
    } catch (err) {
      console.error('Error loading application details:', err);
      showError('Failed to load application details');
    }
  };

  const handleEditApplication = async (application) => {
    try {
      // Fetch full application details
      const response = await apiService.getApplication(application.id);
      const app = response.application;
      
      const transformedApp = {
        id: app.id,
        customer_id: app.customer_id,
        service_id: app.service_id,
        staff_id: app.staff_id,
        status: app.status || 'draft',
        priority: app.priority || 'normal',
        notes: app.notes || '',
        documents: app.documents || [],
        estimated_completion_date: app.estimated_completion_date,
        actual_completion_date: app.actual_completion_date
      };
      
      setSelectedApplication(transformedApp);
      setShowEditApplicationModal(true);
    } catch (err) {
      console.error('Error loading application details:', err);
      showError('Failed to load application details');
    }
  };

  const handleDeleteApplication = async (application) => {
    const confirmed = await showDeleteConfirm(
      `Application #${application.id}`,
      'application'
    );
    
    if (confirmed) {
      try {
        await apiService.deleteApplication(application.id);
        showSuccess('Application deleted successfully');
        loadApplications(); // Reload the list
      } catch (err) {
        console.error('Error deleting application:', err);
        showError(err.message || 'Failed to delete application');
      }
    }
  };

  const handleSaveApplication = async (applicationData) => {
    try {
      await apiService.updateApplication(selectedApplication.id, applicationData);
      showSuccess('Application updated successfully');
      setShowEditApplicationModal(false);
      setSelectedApplication(null);
      loadApplications(); // Reload the list
    } catch (err) {
      console.error('Error updating application:', err);
      showError(err.message || 'Failed to update application');
    }
  };

  const handleCreateApplication = async (applicationData) => {
    try {
      const response = await apiService.createApplication(applicationData);
      console.log('Application created:', response);
      
      // Close modal first
      setShowNewApplicationModal(false);
      
      // Reset filters to 'all' to show the new application
      setStatusFilter('all');
      setPriorityFilter('all');
      setSearchTerm('');
      
      // Small delay to ensure database transaction is complete, then reload
      setTimeout(() => {
        loadApplications();
      }, 500);
      
      showSuccess('Application created successfully');
    } catch (err) {
      console.error('Error creating application:', err);
      showError(err.message || 'Failed to create application');
      throw err; // Re-throw so modal can handle it
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'awaiting_payment': return 'bg-amber-100 text-amber-800';
      case 'partially_paid': return 'bg-orange-100 text-orange-800';
      case 'under_review': return 'bg-purple-100 text-purple-800';
      case 'action_required': return 'bg-rose-100 text-rose-800';
      case 'fully_paid': return 'bg-emerald-100 text-emerald-800';
      case 'submitted_to_authority': return 'bg-blue-100 text-blue-800';
      case 'appointment_scheduled': return 'bg-indigo-100 text-indigo-800';
      case 'processing': return 'bg-cyan-100 text-cyan-800';
      case 'admission_offered': return 'bg-violet-100 text-violet-800';
      case 'booking_confirmed': return 'bg-lime-100 text-lime-800';
      case 'withdrawn_cancelled': return 'bg-slate-100 text-slate-800';
      case 'successful': return 'bg-green-100 text-green-800';
      case 'unsuccessful': return 'bg-red-100 text-red-800';
      case 'ready_for_collection': return 'bg-teal-100 text-teal-800';
      case 'delivered': return 'bg-green-200 text-green-900';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const filteredApplications = applications.filter(app => {
    const matchesSearch = 
      app.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.customerEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.service.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `#${app.id}`.includes(searchTerm.toLowerCase());

    // For staff, optionally show only applications assigned to them (by staff_id)
    const matchesAssignment =
      !showOnlyMyApplications ||
      (user?.role === 'staff' && currentStaffId && app.staff_id === currentStaffId);

    return matchesSearch && matchesAssignment;
  });

  const statusOptions = [{ value: 'all', label: 'All Status' }, ...applicationStatuses];

  const priorityOptions = [
    { value: 'all', label: 'All Priorities' },
    { value: 'low', label: 'Low' },
    { value: 'normal', label: 'Normal' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' }
  ];

  if (loading && applications.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Loading applications...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Applications</h1>
          <p className="text-gray-600 dark:text-gray-400">
            {user?.role === 'customer'
              ? 'Your applications. Apply for services from the Services page.'
              : user?.role === 'agent'
                ? 'Create and manage applications for your customers'
                : 'Manage all customer applications and their status'}
          </p>
        </div>
        {user?.role === 'customer' && (
          <Link to="/app/services" className="btn-primary flex items-center gap-2">
            <Settings className="h-5 w-5" />
            <span>Browse & apply for services</span>
          </Link>
        )}
        {(hasPermission(user, 'applications.create') || user?.role === 'agent') && user?.role !== 'customer' && (
          <button
            onClick={() => setShowNewApplicationModal(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>New Application</span>
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      {/* Status Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <button
          type="button"
          onClick={() => setStatusFilter('all')}
          className={`card text-center transition-all ${statusFilter === 'all' ? 'ring-2 ring-primary-500' : 'hover:shadow-md'}`}
        >
          <div className="text-2xl font-bold text-gray-900">{statusCounts.total}</div>
          <div className="text-sm text-gray-600">Total</div>
        </button>
        {applicationStatuses.map((status) => (
          <button
            key={status.value}
            type="button"
            onClick={() => setStatusFilter(status.value)}
            className={`card text-center transition-all ${statusFilter === status.value ? 'ring-2 ring-primary-500' : 'hover:shadow-md'}`}
          >
            <div className={`text-2xl font-bold ${getStatusColor(status.value).split(' ').find((c) => c.startsWith('text-')) || 'text-gray-900'}`}>
              {statusCounts[status.value] || 0}
            </div>
            <div className="text-sm text-gray-600">{status.label}</div>
          </button>
        ))}
      </div>

      {/* Filters and Search */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search applications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          <div className="md:w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-field"
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="md:w-48">
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="input-field"
            >
              {priorityOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {user?.role === 'staff' && (
            <div className="flex items-center space-x-2">
              <input
                id="my-applications-toggle"
                type="checkbox"
                checked={showOnlyMyApplications}
                onChange={(e) => setShowOnlyMyApplications(e.target.checked)}
                className="h-4 w-4 text-primary-600 border-gray-300 rounded"
              />
              <label htmlFor="my-applications-toggle" className="text-sm text-gray-700">
                Show only applications assigned to me
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Applications Cards */}
      <div className="card">
        {filteredApplications.length === 0 ? (
          <div className="py-8 text-center text-gray-500">
            {loading ? 'Loading...' : 'No applications found'}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filteredApplications.map((app) => (
              <div
                key={app.id}
                className="rounded-xl border border-gray-200 p-4 bg-white hover:shadow-md transition-shadow duration-200"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="flex items-center text-sm font-semibold text-gray-900">
                      <FileText className="h-4 w-4 text-gray-400 mr-2" />
                      #{app.id}
                    </div>
                    <div className="mt-1 text-base font-semibold text-gray-900">{app.customerName}</div>
                    <div className="text-sm text-gray-500">{app.customerEmail || 'No email provided'}</div>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(app.status)}`}>
                    {getStatusLabel(app.status)}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm mb-3">
                  <div>
                    <span className="text-gray-500">Service:</span>{' '}
                    <span className="text-gray-900 font-medium">{app.service}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Priority:</span>{' '}
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        app.priority === 'urgent'
                          ? 'bg-red-100 text-red-800'
                          : app.priority === 'high'
                            ? 'bg-orange-100 text-orange-800'
                            : app.priority === 'normal'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-green-100 text-green-800'
                      }`}
                    >
                      {app.priority?.charAt(0).toUpperCase() + app.priority?.slice(1) || 'Normal'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Source:</span>{' '}
                    <span className="text-gray-900 font-medium">{app.sourceType}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Channel:</span>{' '}
                    <span className="text-gray-900">{app.sourceLabel}</span>
                  </div>
                  <div className="sm:col-span-2">
                    <span className="text-gray-500">Assigned To:</span>{' '}
                    <span className="text-gray-900">{app.assignedTo || 'Unassigned'}</span>
                  </div>
                  <div className="sm:col-span-2 flex items-center text-gray-700">
                    <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                    Created: {app.createdAt}
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                  {(hasPermission(user, 'applications.view') || user?.role === 'agent' || user?.role === 'customer') && (
                    <button
                      onClick={() => handleViewApplication(app)}
                      className="btn-outline text-sm !px-3 !py-2"
                      title="View Application"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </button>
                  )}
                  {hasPermission(user, 'applications.edit') && (
                    <button
                      onClick={() => handleEditApplication(app)}
                      className="btn-secondary text-sm !px-3 !py-2"
                      title="Edit Application"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </button>
                  )}
                  {hasPermission(user, 'applications.delete') && (
                    <button
                      onClick={() => handleDeleteApplication(app)}
                      className="text-red-600 hover:text-red-900 px-3 py-2 rounded-lg hover:bg-red-50"
                      title="Delete Application"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New Application Modal */}
      {showNewApplicationModal && (
        <NewApplicationModal
          onClose={() => setShowNewApplicationModal(false)}
          onSave={handleCreateApplication}
        />
      )}

      {/* Edit Application Modal */}
      {showEditApplicationModal && selectedApplication && (
        <EditApplicationModal
          application={selectedApplication}
          onClose={() => {
            setShowEditApplicationModal(false);
            setSelectedApplication(null);
          }}
          onSave={handleSaveApplication}
        />
      )}

      {/* View Application Modal */}
      {showViewApplicationModal && selectedApplication && (
        <ViewApplicationModal
          application={selectedApplication}
          onClose={() => {
            setShowViewApplicationModal(false);
            setSelectedApplication(null);
          }}
        />
      )}
    </div>
  );
};

export default Applications;
